import { useMemo } from 'react';
import useProspects from './useProspects';
import useDevis from './useDevis';
import useRelances from './useRelances';
import useActivitesCommerciales from './useActivitesCommerciales';

/**
 * Hook combiné pour gérer tout le CRM
 */
export const useCRM = (options = {}) => {
  const { autoLoad = true, filters = {} } = options;

  // Charger tous les modules
  const prospectsHook = useProspects({ autoLoad, filters: filters.prospects || {} });
  const devisHook = useDevis({ autoLoad, filters: filters.devis || {} });
  const relancesHook = useRelances({ autoLoad, filters: filters.relances || {} });
  const activitesHook = useActivitesCommerciales({ autoLoad, filters: filters.activites || {} });

  // Statistiques globales
  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];

    // Prospects
    const prospectsActifs = prospectsHook.prospects.filter(p => p.statut === 'actif').length;
    const prospectsChauds = prospectsHook.prospects.filter(p => p.temperature === 'chaud').length;
    const prospectsTiedes = prospectsHook.prospects.filter(p => p.temperature === 'tiede').length;
    const prospectsFroids = prospectsHook.prospects.filter(p => p.temperature === 'froid').length;
    
    const valeurPotentielleTotal = prospectsHook.prospects
      .filter(p => p.statut === 'actif')
      .reduce((sum, p) => sum + parseFloat(p.valeur_potentielle || 0), 0);

    // Devis
    const devisTotal = devisHook.devis.length;
    const devisEnAttente = devisHook.devis.filter(d => d.statut === 'attente').length;
    const devisAcceptes = devisHook.devis.filter(d => d.statut === 'accepte').length;
    const devisRefuses = devisHook.devis.filter(d => d.statut === 'refuse').length;
    
    const montantDevisAttente = devisHook.devis
      .filter(d => d.statut === 'attente')
      .reduce((sum, d) => sum + parseFloat(d.montant || 0), 0);
    
    const montantDevisAcceptes = devisHook.devis
      .filter(d => d.statut === 'accepte')
      .reduce((sum, d) => sum + parseFloat(d.montant || 0), 0);

    const tauxAcceptation = devisTotal > 0 
      ? Math.round((devisAcceptes / devisTotal) * 100) 
      : 0;

    // Relances
    const relancesTotal = relancesHook.relances.length;
    const relancesUrgentes = relancesHook.relances.filter(
      r => r.priorite === 'urgente' || r.priorite === 'haute'
    ).length;
    const relancesPlanifiees = relancesHook.relances.filter(
      r => r.statut === 'planifie'
    ).length;
    const relancesEnRetard = relancesHook.relances.filter(
      r => r.statut === 'planifie' && r.date_prevue < today
    ).length;

    // Activités
    const activitesTotal = activitesHook.activites.length;
    const activitesAujourdhui = activitesHook.activites.filter(
      a => a.date_activite?.startsWith(today)
    ).length;

    return {
      prospects: {
        total: prospectsHook.prospects.length,
        actifs: prospectsActifs,
        chauds: prospectsChauds,
        tiedes: prospectsTiedes,
        froids: prospectsFroids,
        valeurPotentielle: valeurPotentielleTotal,
      },
      devis: {
        total: devisTotal,
        enAttente: devisEnAttente,
        acceptes: devisAcceptes,
        refuses: devisRefuses,
        montantAttente: montantDevisAttente,
        montantAcceptes: montantDevisAcceptes,
        tauxAcceptation,
      },
      relances: {
        total: relancesTotal,
        urgentes: relancesUrgentes,
        planifiees: relancesPlanifiees,
        enRetard: relancesEnRetard,
      },
      activites: {
        total: activitesTotal,
        aujourdhui: activitesAujourdhui,
      },
    };
  }, [
    prospectsHook.prospects,
    devisHook.devis,
    relancesHook.relances,
    activitesHook.activites,
  ]);

  // État de chargement global
  const loading = 
    prospectsHook.loading || 
    devisHook.loading || 
    relancesHook.loading || 
    activitesHook.loading;

  // Erreur globale
  const error = 
    prospectsHook.error || 
    devisHook.error || 
    relancesHook.error || 
    activitesHook.error;

  // Recharger tout
  const reloadAll = async () => {
    await Promise.all([
      prospectsHook.loadProspects(),
      devisHook.loadDevis(),
      relancesHook.loadRelances(),
      activitesHook.loadActivites(),
    ]);
  };

  return {
    // Données
    prospects: prospectsHook.prospects,
    devis: devisHook.devis,
    relances: relancesHook.relances,
    activites: activitesHook.activites,
    stats,

    // États globaux
    loading,
    error,

    // Actions prospects
    ...prospectsHook,

    // Actions devis
    loadDevis: devisHook.loadDevis,
    createDevis: devisHook.createDevis,
    updateDevis: devisHook.updateDevis,
    deleteDevis: devisHook.deleteDevis,
    envoyerDevis: devisHook.envoyer,
    accepterDevis: devisHook.accepter,
    refuserDevis: devisHook.refuser,

    // Actions relances
    loadRelances: relancesHook.loadRelances,
    createRelance: relancesHook.createRelance,
    updateRelance: relancesHook.updateRelance,
    deleteRelance: relancesHook.deleteRelance,
    marquerRelanceEffectuee: relancesHook.marquerEffectue,
    reporterRelance: relancesHook.reporter,

    // Actions activités
    loadActivites: activitesHook.loadActivites,
    loadActivitesParProspect: activitesHook.loadParProspect,
    createActivite: activitesHook.createActivite,
    updateActivite: activitesHook.updateActivite,
    deleteActivite: activitesHook.deleteActivite,

    // Actions globales
    reloadAll,
  };
};

export default useCRM;
